import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/config";
import { useCustomerGroups } from "@/components/admin/CustomerGroups";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0A0B0E] px-4 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none";

const STATUS_LABEL = {
  sent: "Sent, waiting",
  delivered: "Delivered",
  read: "Read",
  failed: "Not delivered",
  sending: "Sending",
  not_configured: "Not connected",
};

const STATUS_STYLE = {
  sent: "bg-slate-500/15 text-slate-300",
  delivered: "bg-green-400/15 text-green-300",
  read: "bg-emerald-400/20 text-emerald-200",
  failed: "bg-red-400/15 text-red-300",
  sending: "bg-cyan-400/15 text-cyan-200",
};

function fillPreview(body, values) {
  let text = body || "";
  values.forEach((value, i) => {
    const shown = (value || "").replace("{{name}}", "Rahul").replace("{{link}}", "varshasublimation.in");
    text = text.replaceAll(`{{${i + 1}}}`, shown || `{{${i + 1}}}`);
  });
  return text;
}

export function WhatsAppCampaigns({ metaStatus }) {
  const [templates, setTemplates] = useState([]);
  const [templateError, setTemplateError] = useState("");
  const [account, setAccount] = useState("");
  const [showBuilder, setShowBuilder] = useState(false);
  const [draft, setDraft] = useState({
    name: "",
    body: "Namaste {{1}} \u{1F44B}\nVARSHA SUBLIMATION: {{2}}\nOrder now on our website.",
    footer: "Reply STOP to stop these updates",
    header_image: "",
    button_url: "https://sublimationwala.in",
    button_text: "See the catalogue",
  });
  const [selected, setSelected] = useState("");
  const [values, setValues] = useState([]);
  const [headerImage, setHeaderImage] = useState("");
  const [group, setGroup] = useState("");
  const [audience, setAudience] = useState({ recipients: 0, opted_out: 0 });
  const [campaigns, setCampaigns] = useState([]);
  const [progress, setProgress] = useState(null);
  const [products, setProducts] = useState([]);
  const [optOuts, setOptOuts] = useState([]);
  const [showOptOuts, setShowOptOuts] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [testNumber, setTestNumber] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testStatus, setTestStatus] = useState(null);
  const [openCampaign, setOpenCampaign] = useState("");
  const [deliveries, setDeliveries] = useState([]);
  const { groups } = useCustomerGroups();
  const timer = useRef(null);
  const testTimer = useRef(null);

  const connected = !!metaStatus?.configured;

  const loadTemplates = useCallback(() => {
    api.get("/admin/whatsapp/templates")
      .then(({ data }) => { setTemplates(data.templates || []); setTemplateError(data.error || ""); setAccount(data.account || ""); })
      .catch((err) => setTemplateError(formatApiError(err)));
  }, []);

  const loadCampaigns = useCallback(() => {
    api.get("/admin/whatsapp/campaigns").then(({ data }) => setCampaigns(data)).catch(() => {});
  }, []);

  const loadOptOuts = useCallback(() => {
    api.get("/admin/whatsapp/opt-outs").then(({ data }) => setOptOuts(data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadTemplates();
    loadCampaigns();
    loadOptOuts();
    api.get("/products").then(({ data }) => setProducts(data.filter((p) => p.image))).catch(() => {});
  }, [loadTemplates, loadCampaigns, loadOptOuts]);

  useEffect(() => {
    api.get("/admin/whatsapp/audience", { params: { group } })
      .then(({ data }) => setAudience(data))
      .catch(() => {});
  }, [group, optOuts]);

  const template = useMemo(() => templates.find((t) => `${t.name}|${t.language}` === selected), [templates, selected]);

  useEffect(() => {
    if (!template) return;
    setValues(Array.from({ length: template.variables }, (_, i) => (i === 0 ? "{{name}}" : "")));
    setHeaderImage("");
  }, [template]);

  useEffect(() => () => { clearInterval(timer.current); clearInterval(testTimer.current); }, []);

  const watch = (campaignId) => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/whatsapp/campaigns/${campaignId}`);
        setProgress(data);
        if (data.status === "done") {
          clearInterval(timer.current);
          loadCampaigns();
          toast.success(`${data.sent} sent · ${data.failed} failed of ${data.recipients}`);
        }
      } catch {
        clearInterval(timer.current);
      }
    }, 2500);
  };

  const createTemplate = async (kind) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/whatsapp/templates", null, { params: { kind } });
      toast.success(data.message || "Sent to Meta for approval");
      loadTemplates();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitDraft = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/whatsapp/templates/custom", draft);
      toast.success(data.message || "Sent to Meta for approval");
      setShowBuilder(false);
      loadTemplates();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const openDeliveries = async (campaignId) => {
    if (openCampaign === campaignId) {
      setOpenCampaign("");
      return;
    }
    setOpenCampaign(campaignId);
    setDeliveries([]);
    try {
      const { data } = await api.get(`/admin/whatsapp/campaigns/${campaignId}`);
      setDeliveries(data.deliveries || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const watchTest = (wamid) => {
    clearInterval(testTimer.current);
    let tries = 0;
    testTimer.current = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get("/admin/whatsapp/test-status", { params: { wamid } });
        setTestStatus(data);
        if (["delivered", "read", "failed", "undelivered"].includes(data.status) || tries > 15) clearInterval(testTimer.current);
      } catch {
        if (tries > 15) clearInterval(testTimer.current);
      }
    }, 3000);
  };

  const uploadHeaderPhoto = async (file) => {    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Photo is bigger than 5 MB — use a smaller one");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("mode", "banner");
      const { data } = await api.post("/admin/upload-image", form);
      setHeaderImage(data.url);
      toast.success("Photo ready — it will show on top of the message");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const sendTest = async () => {    if (!template) return;
    if (["IMAGE", "VIDEO", "DOCUMENT", "TEXT"].includes(template.header_format) && !headerImage) {
      return toast.error(
        template.header_format === "IMAGE" ? "Choose the photo that goes on top"
          : template.header_format === "VIDEO" ? "Add the video link that goes on top"
            : template.header_format === "DOCUMENT" ? "Add the file link that goes on top"
              : "Write the heading that goes on top"
      );
    }
    setBusy(true);
    setTestStatus(null);
    try {
      const { data } = await api.post("/admin/whatsapp/test-send", {
        template_name: template.name,
        language: template.language,
        variables: values,
        header_image: headerImage,
        header_format: template.header_format || "",
        note: template.body,
      }, { params: { phone: testNumber } });
      if (data.status === "sent") toast.success("Sent — check WhatsApp on that number in a few seconds");
      else toast.error(data.message || "Meta did not accept this message");
      setTestResult(data);
      if (data.wamid) watchTest(data.wamid);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!template) return;
    const message = values.slice(1).join(" ") || values[0] || "";
    if (template.variables > 0 && message.trim().length < 5) return toast.error("Write the update you want to send");
    if (["IMAGE", "VIDEO", "DOCUMENT", "TEXT"].includes(template.header_format) && !headerImage) {
      return toast.error(
        template.header_format === "IMAGE" ? "Choose the photo that goes on top"
          : template.header_format === "VIDEO" ? "Add the video link that goes on top"
            : template.header_format === "DOCUMENT" ? "Add the file link that goes on top"
              : "Write the heading that goes on top"
      );
    }
    if (!window.confirm(`Send this WhatsApp update to ${audience.recipients} registered customer(s)?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/whatsapp/campaigns", {
        template_name: template.name,
        language: template.language,
        variables: values,
        header_image: headerImage,
        header_format: template.header_format || "",
        audience: group ? "group" : "all",
        group,
        note: template.body,
      });
      toast.success(`Sending to ${data.recipients} customers…`);
      setProgress({ ...data, sent: 0, failed: 0, status: "sending", deliveries: [] });
      watch(data.campaign_id);
      loadCampaigns();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const restore = async (customer) => {
    if (!window.confirm(`Start sending updates to ${customer.name || customer.phone} again? Only do this if they asked you to.`)) return;
    try {
      await api.post(`/admin/whatsapp/opt-outs/${customer.id}/restore`);
      toast.success("They will get updates again");
      loadOptOuts();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // WhatsApp hides everything past the first few lines behind "Read more":
  // 3 lines when the template has media on top, 4 otherwise.
  const maxLines = ["IMAGE", "VIDEO", "DOCUMENT"].includes(template?.header_format) ? 3 : 4;
  const previewLines = template ? fillPreview(template.body, values).split("\n") : [];
  const visibleLines = previewLines.slice(0, maxLines);
  const hiddenLines = previewLines.slice(maxLines);

  const approved = templates.filter((t) => t.status === "APPROVED");  const pending = templates.find((t) => t.status !== "APPROVED" && t.status !== "REJECTED");
  const trackingOn = values.some((v) => v.includes("{{link}}"));

  const toggleTracking = () => {
    if (!values.length) return;
    const last = values.length - 1;
    setValues(values.map((v, i) => {
      if (i !== last) return v;
      return trackingOn ? v.replace(/\s*\{\{link\}\}/g, "").trim() : `${v.trim()} {{link}}`.trim();
    }));
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#12141A] p-5" data-testid="campaign-box">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-display text-lg font-bold">New product & offer updates (WhatsApp Business)</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="meta-status">
            {connected ? "Connected to Meta WhatsApp Business" : "Not connected — add your Meta WhatsApp keys"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-[11px] text-cyan-300" data-testid="campaign-recipients">
            {audience.recipients} registered customers
          </span>
          <button
            data-testid="campaign-optouts-toggle"
            onClick={() => { setShowOptOuts((v) => !v); loadOptOuts(); }}
            className="rounded-full border border-white/10 px-3 py-1.5 font-mono text-[11px] text-slate-400 hover:border-fuchsia-400/40 hover:text-fuchsia-300"
          >
            {optOuts.length} said STOP
          </button>
        </div>
      </div>

      {showOptOuts && (
        <div className="mt-3 rounded-xl border border-white/10 bg-black/30 p-3" data-testid="campaign-optouts">
          {optOuts.length === 0 ? (
            <p className="text-xs text-slate-500" data-testid="campaign-optouts-empty">Nobody has stopped your updates. Customers who reply STOP on WhatsApp appear here and are never messaged again.</p>
          ) : (
            <ul className="space-y-2">
              {optOuts.map((c) => (
                <li key={c.id} data-testid={`campaign-optout-${c.id}`} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="text-slate-300">{c.name || "—"} <span className="font-mono text-[11px] text-slate-500">{c.phone}{c.state ? ` · ${c.state}` : ""}</span></span>
                  <button
                    data-testid={`campaign-optout-restore-${c.id}`}
                    onClick={() => restore(c)}
                    className="rounded-full border border-cyan-400/30 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-cyan-300 hover:bg-cyan-400/10"
                  >
                    Start again
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!connected && (
        <div className="mt-3 rounded-xl border border-yellow-300/25 bg-yellow-300/5 p-3.5 text-xs leading-relaxed text-yellow-100" data-testid="meta-setup-hint">
          <b className="text-yellow-300">3 steps to start sending automatically:</b>
          <ol className="mt-2 list-decimal space-y-1 pl-4">
            <li>In Meta Business Suite, add your business number to WhatsApp → API setup</li>
            <li>Save the WhatsApp account ID, phone number ID and permanent token here (ask me in chat, I open a secure box)</li>
            <li>Create the ready-made template from this panel and wait for Meta's approval</li>
          </ol>
          <div className="mt-2">Till then keep using the green WhatsApp buttons on Customers and Orders — they open WhatsApp with the message ready.</div>
        </div>
      )}

      {connected && (
        <div className="mt-4 space-y-3">
          {templateError && <p className="rounded-xl border border-red-400/20 bg-red-400/5 p-3 text-xs text-red-300" data-testid="template-error">{templateError}</p>}
          <div className="flex flex-wrap items-center gap-3">
            <select
              data-testid="campaign-template-select"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="rounded-full border border-white/10 bg-[#0A0B0E] px-4 py-2 text-sm"
            >
              <option value="">Choose an approved template</option>
              {templates.map((t) => (
                <option
                  key={`${t.name}|${t.language}`}
                  value={`${t.name}|${t.language}`}
                  disabled={t.status !== "APPROVED"}
                >
                  {t.name} ({t.language}){t.header_format === "IMAGE" ? " · with photo" : ""}
                  {t.status === "APPROVED" ? "" : t.status === "REJECTED" ? " · rejected by Meta" : " · waiting for Meta approval"}
                </option>
              ))}
            </select>
            <button data-testid="campaign-refresh-templates" onClick={loadTemplates} className="rounded-full border border-white/10 px-4 py-2 text-xs text-slate-300 hover:bg-white/5">
              Refresh list
            </button>
            <button
              data-testid="campaign-new-template"
              onClick={() => setShowBuilder((v) => !v)}
              className="rounded-full border border-cyan-400/40 bg-cyan-400/10 px-4 py-2 text-xs font-bold text-cyan-200"
            >
              {showBuilder ? "Close" : "Create new template"}
            </button>
            {!pending && !templates.some((t) => t.name === "varsha_news") && (
              <button data-testid="campaign-create-template" disabled={busy} onClick={() => createTemplate("text")} className="rounded-full border border-white/10 px-4 py-2 text-xs text-slate-300 hover:bg-white/5 disabled:opacity-40">
                Ready-made text template
              </button>
            )}
            {!pending && !templates.some((t) => t.name === "varsha_offer") && (
              <button data-testid="campaign-create-photo-template" disabled={busy} onClick={() => createTemplate("photo")} className="rounded-full border border-white/10 px-4 py-2 text-xs text-slate-300 hover:bg-white/5 disabled:opacity-40">
                Ready-made photo template
              </button>
            )}
            {!approved.length && !templateError && (
              <span className="font-mono text-[11px] text-yellow-300" data-testid="campaign-no-templates">
                {pending
                  ? `${pending.name} is waiting for Meta approval (usually a few hours) — press Refresh list later`
                  : "No approved template yet — Meta usually approves in a few hours"}
              </span>
            )}
          </div>

          <p className="font-mono text-[10px] leading-relaxed text-slate-500" data-testid="campaign-template-source">
            {templates.length} template{templates.length === 1 ? "" : "s"} read live from WhatsApp account {account || "—"} · if a template you made in Meta is missing here, it belongs to a different WhatsApp Business account — check the account name on top in WhatsApp Manager
          </p>

          {showBuilder && (
            <div className="rounded-xl border border-cyan-400/25 bg-cyan-400/5 p-4" data-testid="template-builder">
              <p className="font-display text-sm font-bold text-cyan-200">Make your own template</p>
              <p className="mt-1 text-[11px] leading-relaxed text-slate-400">
                Write it once, Meta approves it (usually a few hours), then it appears in the list above. Use <b>{"{{1}}"}</b> for the customer name and <b>{"{{2}}"}</b> for the part you change each time. A blank cannot be the first or last thing in the message.
              </p>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Template name</label>
                  <input
                    data-testid="template-name-input"
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    placeholder="diwali_offer"
                    className={`${inputCls} mt-1`}
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Photo on top (optional)</label>
                  <select
                    data-testid="template-photo-select"
                    value={draft.header_image}
                    onChange={(e) => setDraft({ ...draft, header_image: e.target.value })}
                    className={`${inputCls} mt-1`}
                  >
                    <option value="">No photo — text only</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.image}>{p.sku ? `${p.sku} · ` : ""}{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-3">
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Message</label>
                <textarea
                  data-testid="template-body-input"
                  rows={4}
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  className={`${inputCls} mt-1`}
                />
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Small line at the bottom</label>
                  <input
                    data-testid="template-footer-input"
                    value={draft.footer}
                    onChange={(e) => setDraft({ ...draft, footer: e.target.value })}
                    className={`${inputCls} mt-1`}
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Button text · link</label>
                  <div className="mt-1 flex gap-2">
                    <input
                      data-testid="template-button-text-input"
                      value={draft.button_text}
                      onChange={(e) => setDraft({ ...draft, button_text: e.target.value })}
                      className={inputCls}
                    />
                    <input
                      data-testid="template-button-url-input"
                      value={draft.button_url}
                      onChange={(e) => setDraft({ ...draft, button_url: e.target.value })}
                      className={inputCls}
                    />
                  </div>
                </div>
              </div>
              <button
                data-testid="template-submit"
                disabled={busy}
                onClick={submitDraft}
                className="mt-4 rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 px-5 py-2.5 text-xs font-bold text-black transition hover:opacity-90 disabled:opacity-40"
              >
                {busy ? "Sending to Meta…" : "Send for Meta approval"}
              </button>
            </div>
          )}


          {template && (
            <>
              <pre className="whitespace-pre-wrap rounded-xl border border-white/10 bg-black/30 p-3 font-mono text-[11px] text-slate-400" data-testid="campaign-template-body">{template.body}</pre>
              {template.header_format === "IMAGE" && (
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Photo on top</label>
                  <select
                    data-testid="campaign-header-product"
                    value={products.some((p) => p.image === headerImage) ? headerImage : ""}
                    onChange={(e) => setHeaderImage(e.target.value)}
                    className={`${inputCls} mt-1`}
                  >
                    <option value="">Choose the product photo</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.image}>{p.sku ? `${p.sku} · ` : ""}{p.name}</option>
                    ))}
                  </select>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <label
                      data-testid="campaign-header-upload"
                      className="cursor-pointer rounded-full border border-fuchsia-400/40 bg-fuchsia-400/10 px-4 py-2 text-xs font-bold text-fuchsia-200 hover:bg-fuchsia-400/20"
                    >
                      {uploading ? "Uploading…" : "Or upload any photo"}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploading}
                        onChange={(e) => uploadHeaderPhoto(e.target.files?.[0])}
                      />
                    </label>
                    <span className="text-[11px] text-slate-500">Use the same photo you uploaded in Meta, or any offer poster — JPG/PNG under 5 MB.</span>
                  </div>
                  <input
                    data-testid="campaign-header-link"
                    value={headerImage.startsWith("http") ? headerImage : ""}
                    onChange={(e) => setHeaderImage(e.target.value.trim())}
                    placeholder="…or paste a photo link (https://…jpg)"
                    className={`${inputCls} mt-2`}
                  />
                  {headerImage && (
                    <img src={headerImage} alt="Photo on top of the message" data-testid="campaign-header-preview" className="mt-2 h-28 w-28 rounded-lg border border-white/10 bg-white object-contain" />
                  )}
                </div>
              )}
              {["VIDEO", "DOCUMENT", "TEXT"].includes(template.header_format) && (
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    {template.header_format === "VIDEO" ? "Video link that goes on top" : template.header_format === "DOCUMENT" ? "File link that goes on top" : "Heading text on top"}
                  </label>
                  <input
                    data-testid="campaign-header-value"
                    value={headerImage}
                    onChange={(e) => setHeaderImage(e.target.value)}
                    placeholder={
                      template.header_format === "VIDEO"
                        ? "https://… .mp4 link (under 16 MB)"
                        : template.header_format === "DOCUMENT"
                          ? "https://… .pdf link"
                          : "e.g. New launch"
                    }
                    className={`${inputCls} mt-1`}
                  />
                  <p className="mt-1 text-[11px] text-slate-500">
                    {template.header_format === "TEXT"
                      ? "This template shows a heading above your message."
                      : "This template was approved with a " + template.header_format.toLowerCase() + " on top, so WhatsApp needs one every time."}
                  </p>
                </div>
              )}
              {values.map((value, i) => (
                <div key={i}>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Detail {i + 1} {i === 0 ? "(keep {{name}} for the customer's name)" : "(your update, offer or sale)"}
                  </label>
                  <textarea
                    data-testid={`campaign-variable-${i + 1}`}
                    value={value}
                    rows={i === 0 ? 1 : 3}
                    onChange={(e) => setValues(values.map((v, idx) => (idx === i ? e.target.value : v)))}
                    placeholder={i === 0 ? "{{name}}" : "New sublimation mugs just launched at Rs 95 — 10% off till Sunday"}
                    className={`${inputCls} mt-1`}
                  />
                </div>
              ))}
              <label className="flex items-center gap-2 text-xs text-slate-300">
                <input data-testid="campaign-tracking-toggle" type="checkbox" checked={trackingOn} onChange={toggleTracking} disabled={!values.length} />
                {values.length
                  ? "Add your website link so this update's visits and orders are counted"
                  : "This template's text is fixed in Meta, so visits cannot be counted — use a template with your own message to get the report"}
              </label>
              <div className="rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-3 text-sm text-slate-200" data-testid="campaign-preview">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-300">Customer will see</p>
                <p className="mt-1 whitespace-pre-wrap">{visibleLines.join("\n")}</p>
                {!!hiddenLines.length && (
                  <>
                    <p className="mt-1 font-bold text-yellow-300">… Read more</p>
                    <p className="mt-1 whitespace-pre-wrap text-slate-500">{hiddenLines.join("\n")}</p>
                  </>
                )}
              </div>
              {!!hiddenLines.length && (
                <p className="rounded-xl border border-yellow-400/20 bg-yellow-400/5 p-3 text-[11px] leading-relaxed text-yellow-200" data-testid="campaign-readmore-warning">
                  WhatsApp only shows the first {maxLines} lines of this message{template.header_format === "IMAGE" ? " because it has a photo on top" : ""} —
                  the rest hides behind "Read more". Keep the update to {maxLines} short lines (no blank lines, no link inside the text) so customers read everything at once.
                </p>
              )}
            </>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <input
              data-testid="campaign-test-number"
              value={testNumber}
              onChange={(e) => setTestNumber(e.target.value)}
              placeholder="Your number for a test"
              className="w-48 rounded-full border border-white/10 bg-[#0A0B0E] px-4 py-2 text-sm"
            />
            <button
              data-testid="campaign-test-send"
              onClick={sendTest}
              disabled={busy || !template || !testNumber}
              className="rounded-full border border-cyan-400/40 px-4 py-2 text-xs font-bold text-cyan-200 disabled:opacity-40"
            >
              Send test to me
            </button>
            <select data-testid="campaign-group" value={group} onChange={(e) => setGroup(e.target.value)} className="rounded-full border border-white/10 bg-[#0A0B0E] px-4 py-2 text-sm">
              <option value="">All registered customers</option>
              {groups.map((g) => (
                <option key={g.key} value={g.key}>{g.name} ({g.count})</option>
              ))}
            </select>
            <button
              data-testid="campaign-send-btn"
              onClick={send}
              disabled={busy || !template || !audience.recipients}
              className="ml-auto rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 px-6 py-2.5 text-sm font-bold text-[#0A0B0E] disabled:opacity-40"
            >
              {busy ? "Sending…" : `Send update to ${audience.recipients}`}
            </button>
          </div>

          {testResult && (
            <p
              data-testid="campaign-test-result"
              className={`rounded-xl border p-3 text-xs ${testResult.status === "sent" ? "border-green-400/20 bg-green-400/5 text-green-300" : "border-red-400/20 bg-red-400/5 text-red-300"}`}
            >
              {testResult.status !== "sent"
                ? testResult.message
                : testStatus?.status === "read"
                  ? "Delivered and read on that phone ✓"
                  : testStatus?.status === "delivered"
                    ? "Delivered to that phone ✓"
                    : ["failed", "undelivered"].includes(testStatus?.status)
                      ? `Not delivered — ${testStatus.error || "WhatsApp rejected it on the way"}`
                      : "Meta accepted it — checking the delivery receipt…"}
            </p>
          )}

          {progress && (
            <div className="rounded-xl border border-white/10 bg-black/30 p-3 text-sm" data-testid="campaign-progress">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{progress.status === "done" ? "Finished" : "Sending"}</span>
              <span className="ml-3 text-green-400">{progress.sent || 0} sent</span>
              <span className="ml-3 text-red-400">{progress.failed || 0} failed</span>
              <span className="ml-3 text-slate-400">of {progress.recipients}</span>
              {(progress.deliveries || []).filter((d) => d.error).slice(0, 5).map((d) => (
                <p key={d.phone} className="mt-1 text-[11px] text-slate-500">{d.name || d.phone}: {d.error}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {!!campaigns.length && (
        <div className="mt-4 overflow-x-auto rounded-xl border border-white/10" data-testid="campaign-history">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-[#0A0B0E] font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Template</th>
                <th className="px-4 py-3">Sent</th>
                <th className="px-4 py-3">Delivered</th>
                <th className="px-4 py-3">Read</th>
                <th className="px-4 py-3">Not delivered</th>
                <th className="px-4 py-3">Visits</th>
                <th className="px-4 py-3">Orders</th>
                <th className="px-4 py-3">Sales</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <Fragment key={c.id}>
                  <tr
                    data-testid={`campaign-row-${c.id}`}
                    onClick={() => openDeliveries(c.id)}
                    className="cursor-pointer border-b border-white/5 hover:bg-white/5"
                  >
                    <td className="px-4 py-2.5 font-mono text-[11px] text-slate-500">{new Date(c.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-slate-300">{c.template_name}</td>
                    <td className="px-4 py-2.5 text-slate-300">{c.sent}</td>
                    <td className="px-4 py-2.5 text-green-400" data-testid={`campaign-delivered-${c.id}`}>{c.delivered ?? 0}</td>
                    <td className="px-4 py-2.5 text-emerald-300" data-testid={`campaign-read-${c.id}`}>{c.read ?? 0}</td>
                    <td className="px-4 py-2.5 text-red-400" data-testid={`campaign-undelivered-${c.id}`}>{(c.undelivered ?? 0) + (c.failed ?? 0)}</td>
                    <td className="px-4 py-2.5 text-cyan-300" data-testid={`campaign-visits-${c.id}`}>{c.visits ?? 0}</td>
                    <td className="px-4 py-2.5 text-fuchsia-300" data-testid={`campaign-orders-${c.id}`}>{c.orders ?? 0}</td>
                    <td className="px-4 py-2.5 font-mono text-yellow-300" data-testid={`campaign-sales-${c.id}`}>{formatINR(c.sales || 0)}</td>
                  </tr>
                  {openCampaign === c.id && (
                    <tr data-testid={`campaign-deliveries-${c.id}`}>
                      <td colSpan={9} className="bg-black/40 px-4 py-3">
                        {!deliveries.length ? (
                          <p className="text-xs text-slate-500">No customer rows for this update yet.</p>
                        ) : (
                          <ul className="space-y-1.5">
                            {deliveries.map((d) => (
                              <li key={d.phone} data-testid={`campaign-delivery-${d.phone}`} className="flex flex-wrap items-center gap-3 text-sm">
                                <span className="min-w-[180px] text-slate-300">{d.name || "—"} <span className="font-mono text-[11px] text-slate-500">{d.phone}</span></span>
                                <span className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLE[d.status] || "bg-white/5 text-slate-400"}`}>
                                  {STATUS_LABEL[d.status] || d.status}
                                </span>
                                {d.error && <span className="text-[11px] text-red-300">{d.error}</span>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
